// src/pages/Account/AccountPage.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, updateUserProfile, deleteUserAccount, cancelSubscription } from '../../services/chatApi'; // Adjust path if needed
import ProfileSettings from './ProfileSettings';
import SubscriptionManagement from './SubscriptionManagement';
import DangerZone from './DangerZone';

const AccountPage = () => {
    const { t } = useTranslation();
    const { logout, updateUser } = useAuth();
    const navigate = useNavigate();

    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('profile');

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const data = await getUserProfile();
                setProfileData(data);
            } catch (err) {
                setError(t('account.errors.load'));
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [t]);

    const handleUpdate = async (updatedData) => {
        console.log('🔄 Starting profile update...');
        try {
            const newProfile = await updateUserProfile(updatedData);
            console.log('✅ Update successful, received profile:', newProfile);
            if (!newProfile) {
            throw new Error('No data returned from server');
            }
            
            if (!newProfile.user_id && !newProfile.email) {
                throw new Error('Invalid profile data received');
            }

            setProfileData(newProfile);
            updateUser(prev => ({ ...prev, ...newProfile })); // Update global context
            alert(t('account.success.profileUpdated')); // Simple feedback
        } catch (err) {
            console.error('❌ Profile update error caught:', err);
            console.error('Error type:', typeof err);
            console.error('Error name:', err.name);
            console.error('Error message:', err.message);
            alert(t('account.errors.update'));
        }
    };

    const handleCancelSub = async () => {
        if (window.confirm(t('account.subscription.confirmCancel'))) {
            try {
                const updatedProfile = await cancelSubscription();
                setProfileData(updatedProfile);
                updateUser(prev => ({ ...prev, ...updatedProfile }));
                alert(t('account.success.subscriptionCanceled'));
            } catch (err) {
                alert(err.message || t('account.errors.cancelSub'));
            }
        }
    };

    const handleDeleteAccount = async () => {
        if (window.confirm(t('account.dangerZone.confirmDelete'))) {
            try {
                await deleteUserAccount();
                alert(t('account.success.accountDeleted'));
                logout();
                navigate('/login');
            } catch (err) {
                alert(t('account.errors.delete'));
            }
        }
    };

    if (loading) return <div className="text-center p-10">{t('loading')}</div>;
    if (error) return <div className="text-center p-10 text-red-500">{error}</div>;

    const tabs = [
        { key: 'profile', label: t('account.tabs.profile') },
        { key: 'subscription', label: t('account.tabs.subscription') },
        { key: 'danger', label: t('account.tabs.dangerZone') },
    ];

    return (
        <div className="container mx-auto max-w-4xl p-4 md:p-8">
            <h1 className="text-3xl font-bold mb-6">{t('account.title')}</h1>
            <div className="flex border-b border-slate-700 mb-6">
                {tabs.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === tab.key
                                ? 'border-b-2 border-brand-orange text-white'
                                : 'text-slate-400 hover:text-white'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="bg-slate-800 p-6 rounded-lg">
                {activeTab === 'profile' && profileData && (
                    <ProfileSettings userData={profileData} onUpdate={handleUpdate} />
                )}
                {activeTab === 'subscription' && profileData && (
                    <SubscriptionManagement userData={profileData} onCancel={handleCancelSub} />
                )}
                {activeTab === 'danger' && (
                    <DangerZone onDelete={handleDeleteAccount} />
                )}
            </div>
        </div>
    );
};

export default AccountPage;